import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  TablePagination,
  Box,
  Collapse,
  IconButton,
  Typography,
  Chip,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { ActivityLog, ActivityChange } from "../../app/models/activityLog";
import agent from "../../app/api/agent";
import LoadingComponent from "../../app/layout/LoadingComponent";

interface MemberActivityHistoryProps {
  memberId: string;
}

export default function MemberActivityHistory({
  memberId,
}: MemberActivityHistoryProps) {
  const { t } = useTranslation();
  const [activities, setActivities] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadActivities();
  }, [page, pageSize, memberId]);

  const loadActivities = async () => {
    setLoading(true);
    try {
      const filterModel = {
        page: page + 1,
        pageSize: pageSize,
        orderBy: "CreatedAt",
        sortDirection: "desc",
      };

      const response = await agent.ActivityLogs.getMemberActivityLogs(
        memberId,
        filterModel
      );

      setActivities(response.data);
      setTotalRecords(response.totalRecords);
    } catch (error) {
      console.error("Error loading member activities:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const toggleExpandRow = (activityId: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(activityId)) {
      newExpanded.delete(activityId);
    } else {
      newExpanded.add(activityId);
    }
    setExpandedRows(newExpanded);
  };

  const parseChanges = (changesJson: string): ActivityChange[] => {
    try {
      return changesJson ? JSON.parse(changesJson) : [];
    } catch (error) {
      console.error("Error parsing changes:", error);
      return [];
    }
  };

  const translateNote = (note: string, activityName: string): string => {
    if (!note) return "";
    
    if (activityName.includes("Payment") && note.includes("|")) {
      const parts = note.split("|");
      if (parts.length === 3) {
        const [memberNumber, amount, paymentType] = parts;
        return t("activityNotes.paymentNote", {
          memberNumber,
          amount,
          paymentType: t(`paymentTypes.${paymentType}`, paymentType)
        });
      }
    }
    
    if (activityName === "Member approved" && note.includes("|")) {
      const parts = note.split("|");
      if (parts.length === 2) {
        const [memberName, memberNumber] = parts;
        return t("activityNotes.memberApproved", { memberName, memberNumber });
      }
    }
    
    if (activityName === "Online member registration" && note.includes("|")) {
      const parts = note.split("|");
      if (parts.length === 2) {
        const [memberName, memberNumber] = parts;
        return t("activityNotes.onlineMemberRegistration", { memberName, memberNumber });
      }
    }
    
    if (activityName === "Member updated" && note.includes("|")) {
      const parts = note.split("|");
      if (parts.length === 2 && parts[1] === "fields") {
        const count = parseInt(parts[0]);
        if (count === 0) {
          return t("activityNotes.memberUpdatedNoChanges");
        }
        return t("activityNotes.memberUpdated", { count });
      }
    }
    
    return note;
  };

  if (loading && activities.length === 0) {
    return (
      <LoadingComponent content={t("general.loading")} />
    );
  }

  return (
    <Box>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
              <TableCell style={{ width: 50 }} />
              <TableCell>{t("activityTable.nameHeader") || "Action"}</TableCell>
              <TableCell>
                {t("activityTable.createdByHeader") || "Created By"}
              </TableCell>
              <TableCell>{t("activityTable.noteHeader") || "Details"}</TableCell>
              <TableCell align="right">
                {t("activityTable.createdAtHeader") || "Date"}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {activities.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  {t("dataTable.noData")}
                </TableCell>
              </TableRow>
            ) : (
              activities.map((activity) => [
                <TableRow key={activity.id} hover>
                  <TableCell style={{ width: 50 }}>
                    {activity.changes && (
                      <IconButton
                        size="small"
                        onClick={() => toggleExpandRow(activity.id!)}
                      >
                        {expandedRows.has(activity.id!) ? (
                          <KeyboardArrowUpIcon />
                        ) : (
                          <KeyboardArrowDownIcon />
                        )}
                      </IconButton>
                    )}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>
                    {t(`activityNames.${activity.name}`) || activity.name}
                  </TableCell>
                  <TableCell>{activity.createdByUserName || "System"}</TableCell>
                  <TableCell sx={{ maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis" }}>
                    {translateNote(activity.note || "", activity.name || "")}
                  </TableCell>
                  <TableCell align="right">
                    {activity.createdAt
                      ? new Date(activity.createdAt).toLocaleString()
                      : ""}
                  </TableCell>
                </TableRow>,
                activity.changes && expandedRows.has(activity.id!) && (
                  <TableRow key={`${activity.id}-details`}>
                    <TableCell colSpan={5} sx={{ py: 0, backgroundColor: "#fafafa" }}>
                      <Collapse in={true} timeout="auto" unmountOnExit>
                        <Box sx={{ m: 2 }}>
                          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                            {t("activityTable.changesHeader") || "Changes"}
                          </Typography>
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                            {parseChanges(activity.changes).map((change, idx) => (
                              <Box key={idx} sx={{ display: "flex", gap: 1, alignItems: "flex-start", ml: 1 }}>
                                <Chip label={t(`fieldLabels.${change.field}`, change.fieldLabel || change.field)} size="small" variant="outlined" sx={{ minWidth: 120 }} />
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="body2">
                                    <strong>{t("activityTable.oldLabel")}:</strong> {change.oldValue === "(not set)" ? t("activityTable.notSet") : change.oldValue === "(deleted)" ? t("activityTable.deleted") : change.oldValue}
                                  </Typography>
                                  <Typography variant="body2" color="primary">
                                    <strong>{t("activityTable.newLabel")}:</strong> {change.newValue === "(not set)" ? t("activityTable.notSet") : change.newValue === "(deleted)" ? t("activityTable.deleted") : change.newValue}
                                  </Typography>
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                ),
              ]).filter(Boolean)
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={totalRecords}
        rowsPerPage={pageSize}
        page={page}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handlePageSizeChange}
      />
    </Box>
  );
}
